// Display logic for utm_sources rows (source, medium, campaign, referrer).
// Used by src/components/UtmAttribution.vue. Pure functions so they can be unit tested.

export const INTERNAL_SOURCE = 'internal'
export const BOOKING_SITE_SOURCE = 'booking_site'
export const DEFAULT_MEDIUM = 'organic'
export const DEFAULT_CAMPAIGN = 'organic'


function clean(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

// "booking_site" -> "Booking site", "cpc" -> "Cpc"
export function formatUtmValue(value) {
  const s = clean(value).replace(/_/g, ' ')
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Returns one of: 'none' | 'internal' | 'direct' | 'tracked'
export function utmState({ source, medium, campaign, referrer } = {}) {
  const src = clean(source)
  if (!src) return 'none'
  if (src === INTERNAL_SOURCE) return 'internal'
  const med = clean(medium)
  const camp = clean(campaign)
  const ref = clean(referrer)
  const mediumIsDefault = !med || med === DEFAULT_MEDIUM
  const campaignIsDefault = !camp || camp === DEFAULT_CAMPAIGN
  if (src === BOOKING_SITE_SOURCE && mediumIsDefault && campaignIsDefault && !ref) return 'direct'
  return 'tracked'
}

// Text for the badge: the formatted source ("Booking site", "Internal", "Google").
export function utmChipLabel(utm = {}) {
  if (utmState(utm) === 'none') return ''
  return formatUtmValue(utm.source)
}

// Rows for the tooltip / stat row. Empty array means "nothing to expand" (internal).
// Direct booking-site records list their organic defaults honestly.
export function utmDetailRows(utm = {}) {
  const state = utmState(utm)
  if (state === 'none' || state === 'internal') return []
  const rows = [
    { key: 'source', label: 'Source', value: formatUtmValue(utm.source) || '-' },
    { key: 'medium', label: 'Medium', value: formatUtmValue(utm.medium) || '-' },
    { key: 'campaign', label: 'Campaign', value: formatUtmValue(utm.campaign) || '-' },
  ]
  const ref = clean(utm.referrer)
  if (ref) rows.push({ key: 'referrer', label: 'Referrer', value: ref, raw: true })
  return rows
}

// True when hovering the chip should open a popover.
export function utmHasDetail(utm = {}) {
  const state = utmState(utm)
  return state === 'direct' || state === 'tracked'
}

// Referrer hostname for the compact line ("google.com"); falls back to the raw value.
export function utmReferrerHost(referrer) {
  const ref = clean(referrer)
  if (!ref) return ''
  try {
    return new URL(ref).hostname.replace(/^www\./, '')
  } catch (e) {
    return ref
  }
}

// Segments for the compact one-line form: source always; medium and campaign only
// when set and not the organic default; referrer as hostname when stored.
export function utmLineSegments(utm = {}) {
  const state = utmState(utm)
  if (state === 'none') return []
  if (state !== 'tracked') return [{ key: 'source', value: utmChipLabel(utm) }]
  const segs = [{ key: 'source', value: formatUtmValue(utm.source) }]
  const med = clean(utm.medium)
  const camp = clean(utm.campaign)
  if (med && med !== DEFAULT_MEDIUM) segs.push({ key: 'medium', value: formatUtmValue(med) })
  if (camp && camp !== DEFAULT_CAMPAIGN) segs.push({ key: 'campaign', value: formatUtmValue(camp) })
  const host = utmReferrerHost(utm.referrer)
  if (host) segs.push({ key: 'referrer', value: host })
  return segs
}
